import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnInit, Signal, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { assign, cloneDeep, isNil } from 'lodash';
import {
  AccordionComponent,
  AccordionPanelComponent,
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  CheckboxDescription,
  FormGroupComponent,
  InputComponent,
  MbsSize,
  MbsType,
  ModalFooterDirective,
  SelectComponent,
  StatusComponent,
  TableCellDirective,
  TableGridComponent
} from 'mbs-ui-kit';
import { ModalComponent } from 'mbs-ui-kit/modal/modal.component';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import { SharedPersistentStateEnum } from 'mbs-ui-kit/services/storage/shared-persistent-state-enum';
import { TableHeader } from 'mbs-ui-kit/table-grid/models/table-header';
import Toast from 'mbs-ui-kit/toast/toast.model';
import { ToastService } from 'mbs-ui-kit/toast/toast.service';
import { MbsPopupType } from 'mbs-ui-kit/utils/enums/mbs-popup-enum';
import { MbsValidators } from 'mbs-ui-kit/utils/mbs-validators';
import { ErrorData } from 'mbs-ui-shared@components/report-problem-modal/report-problem.types';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { SupportPortalFacade } from 'mbs-ui-shared@facades/support-portal.facade';
import { Administrator, StorageType } from 'mbs-ui-shared@models';
import Computer, { AgentType, convertAgentTypeToNeededCase, SendLogsExtraOptions } from 'mbs-ui-shared@models/Computer';
import { ComputerBasedModal } from 'mbs-ui-shared@models/ComputersModals';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { PlanInfo } from 'mbs-ui-shared@models/backup/plan-info-model';
import { RecoveryErrorDetails } from 'mbs-ui-shared@models/backup/recovery-error-details.interface';
import { AgentRemoteCommand, ParamsSendLogs, RemoteCommandType } from 'mbs-ui-shared@models/rm/remote-command-model';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { RmCommandsService } from 'mbs-ui-shared@services/rm-commands.service';
import { AgentProviderLocalDateTime } from 'mbs-ui-shared@utils/date';
import { DateTimeFormatterHelper } from 'mbs-ui-shared@utils/helpers/dateTimeFormatterHelper';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { storageAccountsToDestinationsArray } from 'mbs-ui-shared@utils/storage-accounts-to-destinations-array';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';
import { BehaviorSubject, EMPTY, map, noop, Observable, of, switchMap, take } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-computers-send-logs-modal',
  templateUrl: './report-problem-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    NgbCollapseModule,
    CanPipe,
    NgbTooltipModule,
    FormGroupComponent,
    AccordionComponent,
    AccordionPanelComponent,
    AlertComponent,
    I18NextPipe,
    ModalComponent,
    StatusComponent,
    TableGridComponent,
    CheckboxComponent,
    InputComponent,
    TableCellDirective,
    ModalFooterDirective,
    CheckboxDescription,
    SelectComponent,
    StorageTypeIconPipe
  ]
})
export class ReportProblemModalComponent implements ComputerBasedModal, OnInit {
  private auth = inject(AuthFacade);
  private rmCommandsService = inject(RmCommandsService);
  private ability = inject(AbilityService);
  protected readonly PermissionsEnum = PermissionsEnum;
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  private fb = inject(FormBuilder);
  private i18nPipe = inject(I18NextPipe);
  private backupFacade = inject(ComputerBackupFacade);
  private supportPortalFacade = inject(SupportPortalFacade);

  @ViewChild('errorTemplate', { static: true }) private errorTemplate: TemplateRef<any>;
  @ViewChild(ModalComponent, { static: true }) private baseModal: ModalComponent;
  @ViewChild('alertElementRef', { static: false, read: ElementRef }) private alertElementRef: ElementRef<HTMLElement>;

  public readonly mbsSize = MbsSize;
  public readonly MbsPopupType = MbsPopupType;
  public readonly statusType = MbsType;
  public readonly AgentType = AgentType;
  public readonly sendLogsTitle: string;
  public readonly sharedPersistentStateEnum = SharedPersistentStateEnum;
  public readonly placeholderText = 'example1@gmail.com,example2@gmail.com';
  public readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public computer: Computer; // it's recorded outside component
  public plan: PlanInfo; // it's recorded outside component
  public initialErrorData: ErrorData; // it's recorded outside component

  public formGroup: UntypedFormGroup;
  public computerName = '';
  public destinationsByHid: Signal<Array<StorageConnection>>;

  public isHideErrorDetail = true;

  // headers for table in modal with error
  public headers: TableHeader[] = [
    {
      name: 'Icon',
      class: '-stretch pl-0',
      gridColSize: '1fr',
      gridColMin: '150px'
    },
    {
      name: 'Status Bar',
      gridColSize: '250px',
      gridColMin: '250px',
      class: '-stretch text-end'
    }
  ];
  // subheaders for table in modal with error
  public subHeaders: TableHeader[] = [
    {
      name: 'Message',
      class: '-stretch pl-0',
      gridColSize: '1fr'
    }
  ];

  public get isSelectedParams(): boolean {
    const sendToSupportOrSuperAdmin = this.formGroup.get('sendToSuperAdmin').value || this.formGroup.get('params.sendToSupport').value;

    const sendToCustomEmail = this.formGroup.get('params.sendToCustomEmail').value;
    const customEmails = this.formGroup.get('params.customEmails').valid;

    return sendToSupportOrSuperAdmin || (sendToCustomEmail && customEmails);
  }

  public get canSendEventLogs(): boolean {
    const validVersion = '7.1.1';
    const backupAgent = Computer.getAgent(this.computer, AgentType.Backup);

    return (
      !isNil(backupAgent) &&
      versionCompare(backupAgent.version, validVersion) > 0 &&
      !Computer.isNetBuild(this.computer) &&
      this.ability.can('read', PermissionsEnum.Backup)
    );
  }

  public get canSendDatabase(): boolean {
    const validVersion = '8.2';
    const backupAgent = Computer.getAgent(this.computer, AgentType.Backup);

    if ((!Computer.isWindows(this.computer) && !Computer.isNetBuild(this.computer)) || !this.ability.can('read', PermissionsEnum.Backup)) {
      return false;
    }

    return !isNil(backupAgent) && versionCompare(backupAgent.version, validVersion) >= 0;
  }

  public get canSendNetwork(): boolean {
    return this.canSendDatabase && this.ability.can('read', PermissionsEnum.Backup);
  }

  public get canSendMemoryDumps(): boolean {
    const validVersion = '8.3';
    const backupAgent = Computer.getAgent(this.computer, AgentType.Backup);

    if (!Computer.isWindows(this.computer) || !this.ability.can('read', PermissionsEnum.Backup)) {
      return false;
    }

    return !isNil(backupAgent) && versionCompare(backupAgent.version, validVersion) >= 0;
  }

  public get canSendDestinationListing(): boolean {
    const validVersion = '8.7.5';
    const backupAgent = Computer.getAgent(this.computer, AgentType.Backup);

    if ((!Computer.isWindows(this.computer) && !Computer.isNetBuild(this.computer)) || !this.ability.can('read', PermissionsEnum.Backup)) {
      return false;
    }

    return !isNil(backupAgent) && versionCompare(backupAgent.version, validVersion) >= 0;
  }

  public get isAnyAgentSelectable(): boolean {
    return (
      this.ability.can('read', PermissionsEnum.Backup) ||
      this.ability.can('read', PermissionsEnum.Rmm) ||
      this.ability.can('read', PermissionsEnum.ShowRemoteConnection)
    );
  }

  public get isLastRunInfo(): boolean {
    return !!this.plan?.lastRunInfo;
  }

  public get isDisabled() {
    if (!this.isSelectedParams) {
      return true;
    }
    return !(this.formGroup.get('agentCommands').valid && this.isSelectedParams) && !!this.computer;
  }

  constructor() {
    this.sendLogsTitle = this.i18nPipe.transform('shared:sendLogs.title.sendLogsName', { format: 'title' });
  }

  ngOnInit(): void {
    this.initForm();
    this.initStreams();
  }

  initForm(): void {
    this.formGroup = this.fb.group({
      agentCommands: this.fb.group(
        {
          backup: [false],
          rmm: [false],
          ra: [false]
        },
        { validators: [Validators.required, this.agentCommandsValidator.bind(this)] }
      ),
      includeEventLogs: [false],
      includeDatabase: [false],
      includeMemoryDumps: [false],
      includeNetworkDiagnostics: [false],
      includeDestinationListing: [false],
      destinationListing: [],
      params: this.fb.group({
        profile: [''],
        sendToSupport: [!this.ability.can('read', 'Readonly') && !this.ability.can('read', 'SuperAdmin')],
        sendToCustomEmail: [false],
        customEmails: ['', [this.emailMultiValidator.bind(this), Validators.required]]
      }),
      message: [''],
      ticket: [''],
      sendToSuperAdmin: [this.ability.can('read', 'SuperAdmin')]
    });

    if (this.isLastRunInfo && this.isAgentAvailable(AgentType.Backup)) {
      this.formGroup.get('agentCommands').get(AgentType.Backup).setValue(true, { emitValue: false });
    }

    if (!this.isLastRunInfo && this.computer) return;

    this.setIncludeEventLogs(true);
  }

  expandPlanDetailClickHandler(): void {
    this.isHideErrorDetail = !this.isHideErrorDetail;
  }

  /*
   * Validation for the presence of at least one agent
   * @param agentCommands
   * @private
   */
  private agentCommandsValidator(agentCommands: UntypedFormGroup): ValidationErrors | null {
    const Backup = agentCommands.get('backup').value;
    const RMM = agentCommands.get('rmm').value;
    const RA = agentCommands.get('ra').value;

    if (Backup || RMM || RA) {
      return null;
    }

    return { agent: { message: 'Not any agent type selected' } };
  }

  /*
   * Checking for at least one email address in a line (control)
   * @param control
   * @private
   */
  private emailMultiValidator(control: AbstractControl): ValidationErrors {
    const value: string = control.value;

    if (!value) {
      return null;
    }

    const invalidEmails = value
      .split(',')
      .map((email: string) => MbsValidators.emailValidator({ value: email.trim() } as AbstractControl))
      .filter(Boolean);

    return invalidEmails.length > 0 ? invalidEmails[0] : null;
  }

  initStreams(): void {
    if (this.canSendDestinationListing) {
      this.backupFacade.loadDestinations({ hid: this.computer.hid });
      this.destinationsByHid = computed(() => {
        const allDestinations = this.backupFacade.destinationsByHid(this.computer.hid)();

        return storageAccountsToDestinationsArray(allDestinations).filter((storage) => storage.StorageType !== StorageType.AzureVM);
      });
    }

    this.auth.currentUser$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((user: Administrator) => {
      this.computerName = this.computer?.displayName || this.computer?.name || '';
      this.formGroup.patchValue({
        params: {
          profile: this.computer?.profileName || '',
          customEmails: user.Email
        }
      });
    });
  }

  /*
   * Send payload by type command
   */
  handleResolve(): void {
    this.loading$.next(true);

    if (!this.computer && this.initialErrorData) {
      return void this.convertExtraOptionsToMessage(true)
        .pipe(
          take(1),
          switchMap((message) => {
            const params = this.formGroup.get('params').value as ParamsSendLogs;
            const customEmails = params.sendToCustomEmail ? this.prepareCustomEmail(params.customEmails) : [];

            const sendLogsParams: Record<string, unknown> = {
              sendToSupport: params.sendToSupport,
              sendToCustomEmail: params.sendToCustomEmail,
              customEmails,
              message
            };

            return this.supportPortalFacade.sendReportProblem(sendLogsParams);
          }),
          untilDestroyed(this)
        )
        .subscribe({
          next: () => {
            this.toastService.toast(
              new Toast({
                header: this.sendLogsTitle,
                content: this.i18nPipe.transform('shared:sendLogs.texts.logsHaveBeenSent'),
                type: MbsPopupType.success
              })
            );
            this.baseModal.close();
          }
        });
    }

    this.prepareAgentCommands()
      .pipe(
        switchMap((agentCommands) => {
          return this.rmCommandsService.groupRemoteCommand({
            commandType: RemoteCommandType.SendLogs,
            agentCommands // Created for RMM do not use in prod (EM)
          });
        }),
        finalize(() => this.loading$.next(false)),
        catchError(() => EMPTY)
      )
      .subscribe({
        next: (state) => {
          this.displayingResult(state);
          this.baseModal.close();
        }
      });
  }

  /*
   * Prepare agentCommands before PUT on server
   * @private
   */
  private prepareAgentCommands(): Observable<AgentRemoteCommand<ParamsSendLogs>[]> {
    return this.convertExtraOptionsToMessage().pipe(
      map((message: string) => {
        const backup = this.formGroup.get('agentCommands.backup').value;
        const computerHid = this.computer?.hid;
        const agentCommands = this.formGroup.get('agentCommands').value;
        const params = this.formGroup.get('params').value as ParamsSendLogs;

        const customEmails = params.sendToCustomEmail ? this.prepareCustomEmail(params.customEmails) : [];

        if (this.ability.can('read', 'SuperAdmin')) {
          params['sendToSuperAdmin'] = this.formGroup.get('sendToSuperAdmin').value;
        }

        return Object.entries(agentCommands)
          .filter(([, value]) => !!value)
          .map(([key]) => {
            const agentType = key.toLowerCase() as AgentType;
            const destinationListing = this.formGroup.get('destinationListing').value || [];

            const includeEventLogs =
              agentType === AgentType.Backup && this.canSendEventLogs && backup ? this.formGroup.get('includeEventLogs').value : null;
            const includeDatabase =
              agentType === AgentType.Backup && this.canSendDatabase && backup ? this.formGroup.get('includeDatabase').value : null;
            const includeMemoryDumps =
              agentType === AgentType.Backup && this.canSendMemoryDumps && backup ? this.formGroup.get('includeMemoryDumps').value : null;
            const includeNetworkDiagnostics =
              agentType === AgentType.Backup && this.canSendNetwork && backup
                ? this.formGroup.get('includeNetworkDiagnostics').value
                : null;
            const includeDestinationListing =
              agentType === AgentType.Backup && this.canSendDestinationListing && backup
                ? this.formGroup.get('includeDestinationListing').value
                : null;
            const accountsForListing = includeDestinationListing && destinationListing.length ? destinationListing.map((v) => v.ID) : null;
            return {
              computerHid,
              agentType,
              params: assign(
                {},
                params,
                { message, customEmails },
                !isNil(includeEventLogs) && { includeEventLogs },
                !isNil(includeDatabase) && { includeDatabase },
                !isNil(includeMemoryDumps) && { includeMemoryDumps },
                !isNil(includeNetworkDiagnostics) && { includeNetworkDiagnostics },
                !isNil(accountsForListing) && { accountsForListing }
              )
            };
          });
      })
    );
  }

  private convertExtraOptionsToMessage(withInitial = false): Observable<string> {
    const ticket: string = this.formGroup.get('ticket').value.trim();
    const description: string = this.formGroup.get('message').value.trim();
    const flatted = (obj) =>
      Object.values(obj)
        .map((value: string) => value)
        .join('\n');

    if (withInitial) {
      const extraOptions: SendLogsExtraOptions = {
        ticket: `Ticket: ${ticket}`,
        description: `Problem Description: ${description}`
      };
      if (this.initialErrorData.url) extraOptions.url = 'Url: ' + this.initialErrorData.url;
      if (this.initialErrorData.traceId) extraOptions.traceId = 'Trace Id: ' + this.initialErrorData.traceId;
      if (this.initialErrorData.title) extraOptions.title = 'Error Title: ' + this.initialErrorData.title;
      if (this.initialErrorData.message) extraOptions.message = 'Error Detail: ' + this.initialErrorData.message;

      return of(flatted(extraOptions));
    }

    if (this.isLastRunInfo) {
      return DateTimeFormatterHelper.setTime(
        of(this.plan.lastRunInfo?.dateTimeUTC as string),
        this.backupFacade.getAgentUtcOffsetValue(this.computer.hid),
        this.auth.currentUser$.pipe(map((user) => user?.ProviderInfo?.TimeZoneOffset)),
        this.i18nPipe.transform('shared:unknown')
      ).pipe(
        take(1),
        map((dateTime: AgentProviderLocalDateTime) => {
          const errMessage: string = this.plan.lastRunInfo?.errorDetails
            ?.map((errorDetail: RecoveryErrorDetails) => `${errorDetail.detail} (code: ${errorDetail.id})`)
            .join();

          const extraOptions: SendLogsExtraOptions = {
            ticket: `Ticket: ${ticket}`,
            plan: `Plan: ${this.plan.displayName ?? ''} (${this.plan.id ?? ''})`,
            lastRun: dateTime.isEmpty
              ? ''
              : `Last Run: Provider (${dateTime.provider.time}${dateTime.provider.utc ? ' ' + dateTime.provider.utc : ''}) Agent (${
                  dateTime.agent.time
                } ${dateTime.agent.utc ? ' ' + dateTime.agent.utc : ''})`,
            error: `Error: ${errMessage || ''}`,
            description: `Problem Description: ${description}`
          };

          return flatted(extraOptions);
        })
      );
    } else {
      const extraOptions: SendLogsExtraOptions = {
        ticket: `Ticket: ${ticket}`,
        description: `Problem Description: ${description}`
      };

      return of(flatted(extraOptions));
    }
  }

  private prepareCustomEmail(emails: string | string[]): string[] {
    return typeof emails === 'string'
      ? emails
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean)
      : emails;
  }

  /*
   * Show result Send logs in toast
   * @param state
   * @private
   */
  private displayingResult(state): void {
    const prepareState = cloneDeep(state);
    const newResults = [];

    prepareState.resultList.forEach((item) => {
      const found = newResults.find((res) => res?.hid === item.hid);

      if (found) {
        found.childs.push({ ...item.result, agentType: convertAgentTypeToNeededCase(item.agentType) });
      } else {
        item.childs = [{ ...item.result, agentType: convertAgentTypeToNeededCase(item.agentType) }];
        newResults.push(item);
      }
    });

    newResults.forEach((res) => {
      delete res.agentType;
      delete res.result;
    });

    prepareState.resultList = newResults;

    if (prepareState?.ok) {
      prepareState.isErrorsInResultList ? this.handleShowRejectToast(prepareState) : this.handleShowResolveToast(prepareState);
    } else {
      this.toastService.toast(
        new Toast({
          header: this.sendLogsTitle,
          content: `The command <span class="fw-semibold">Send Log</span> is not defined`,
          type: MbsPopupType.danger
        })
      );
    }
  }

  /*
   * Check the computer has agent (application)
   * @param agentType
   */
  public isAgentTypeByApplicationID(agentType: AgentType): boolean {
    return Computer.hasAgent(this.computer, agentType);
  }

  // A stopped agent cannot take the log-collection command, so an installed but offline one stays unselectable (MBS-32674)
  public isAgentAvailable(agentType: AgentType): boolean {
    return this.isAgentTypeByApplicationID(agentType) && Computer.isAgentOnline(this.computer, agentType);
  }

  /*
   * Show resolve toast for Send logs success result
   * @param prepareState
   * @private
   */
  private handleShowResolveToast(prepareState): void {
    const getContentText = (): string => {
      const compSendLogsResult = prepareState.resultList[0];
      const name = compSendLogsResult?.name ?? '';
      const displayName = compSendLogsResult?.displayName ?? '';

      return 'Logs have been sent for ' + displayName.trim().toLowerCase() !== name.trim().toLowerCase()
        ? `${displayName} [${name}]`
        : name;
    };

    this.toastService.toast(
      new Toast({
        header: this.sendLogsTitle,
        content: getContentText(),
        type: MbsPopupType.success,
        icon: true
      })
    );
  }

  /*
   * Show reject toast for Send logs failed result
   * @param prepareState
   * @private
   */
  private handleShowRejectToast(prepareState): void {
    this.toastService.toast(
      new Toast({
        header: this.sendLogsTitle,
        content: prepareState.message,
        type: MbsPopupType.danger,
        icon: true,
        buttons: [
          {
            clickHandler: function (): void {
              this.handleOpenModalWithDetail(prepareState, this.sendLogsTitle);
            }.bind(this)
          }
        ]
      })
    );
  }

  private handleOpenModalWithDetail(content, title: string): void {
    const settings: ModalSettings = {
      collapsing: true,
      header: {
        title: `${title} details`,
        showExpandedCross: true,
        textOverflow: true
      },
      data: {
        context: content
      },
      size: MbsSize.lg
    };

    this.modalService.open(settings, this.errorTemplate).catch(noop);
  }

  /*
   * Recursively unchecks checkbox `includeEventLogs`
   * @param state
   */
  public handleChangeIncludeEventLogs(state: boolean): void {
    this.setIncludeEventLogs(state);
  }

  handleChangeToSupport(state: boolean): void {
    const modalContent = (this.baseModal as any)?.element.nativeElement.closest('.modal-content');
    const rect = this.alertElementRef?.nativeElement.getBoundingClientRect();
    const marginTop = 7; // [descriptionClasses]="'mt-2'"
    const offset = state ? 0 : ((Math.floor(rect?.height || 0) + marginTop) / 2) * -1;

    (modalContent as HTMLElement).style.transform = `translateY(${offset}px)`;
  }

  handleRedirectToSP(): void {
    this.auth.redirectToSupportPortal();
  }

  private setIncludeEventLogs(value: boolean): void {
    if (this.canSendEventLogs) {
      this.formGroup.get('includeEventLogs').patchValue(value);
    }

    if (this.canSendNetwork) {
      this.formGroup.get('includeNetworkDiagnostics').patchValue(value);
    }

    if (this.canSendDatabase && !value) {
      this.formGroup.get('includeDatabase').patchValue(value);
    }

    if (this.canSendMemoryDumps && !value) {
      this.formGroup.get('includeMemoryDumps').patchValue(value);
    }
  }
}
